import PropTypes from "prop-types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useFormContext, useWatch } from "react-hook-form";
import get from "lodash/get";

import { cn } from "@/lib/utils";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  FormCombobox,
  FormDatePicker,
  FormInput,
  RemoveButton,
} from "components/BaseForm";
import { buildPrice, getInvoicesRowAmount } from "components/helpers";
import {
  SPECIAL_LINE_TYPES,
  isSpecialLine,
  isSpecialLineValue,
  lineFlagsFor,
} from "routes/revenue_opportunity/form/invoiceLines";
import { useRuvixxFlags } from "providers/RuvixxFlagsProvider";
import {
  AllowedTermField,
  PricedAmountField,
} from "routes/revenue_opportunity/form/sections/invoices/PricingFields";
import ProductService from "services/Product";
import RevenueOpportunityService from "services/RevenueOpportunities";

const productQuery = productId => ({
  queryKey: ["product", productId],
  queryFn: () => ProductService.get(productId),
});

const OVERRIDE = "total_value_override";

const SPECIAL_OPTIONS = SPECIAL_LINE_TYPES.map(type => ({
  key: type.value,
  value: type.value,
  text: type.label,
  isSpecial: true,
}));

const renderProductOption = option =>
  option.isSpecial ? (
    <span className="tw:italic">{option.text}</span>
  ) : (
    option.text
  );

// Top-aligned and wrapping, so a validation message grows its row downward
// under its own field instead of widening the column or re-centring the
// other inputs.
const CELL = "tw:align-top tw:whitespace-normal";
const isPositiveInteger = value => /^\d+$/.test(String(value)) && +value >= 1;

const InvoiceRow = ({
  index,
  productOptions,
  disabledProductOptions,
  hasOtherOverride,
  onRemove,
}) => {
  const {
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();
  const queryClient = useQueryClient();
  const { enable_product_pricing: isPricingEnabled } = useRuvixxFlags();
  const line = useWatch({ name: `invoices.${index}` });
  const special = isSpecialLine(line);
  const isCatalogLine = !special && typeof line.product_id === "number";
  const { data: lineProduct } = useQuery({
    ...productQuery(line.product_id),
    enabled: Boolean(isPricingEnabled) && isCatalogLine,
  });
  const path = field => `invoices.${index}.${field}`;
  const set = (field, value) =>
    setValue(path(field), value, { shouldDirty: true });

  // A disabled product is listed only on the line that already uses it, so it
  // keeps its label there but cannot be picked anywhere else.
  const options = [
    ...productOptions,
    ...disabledProductOptions.filter(o => o.value === line.product_id),
    ...SPECIAL_OPTIONS.filter(o => o.value !== OVERRIDE || !hasOtherOverride),
  ];

  const recalculateEndDate = async (startDate, term) => {
    if (!startDate || !isPositiveInteger(term)) return;
    const { license_end_date } =
      await RevenueOpportunityService.calculateLicenseEndDate({
        license_start_date: startDate,
        term,
      });
    set("license_end_date", license_end_date);
  };

  const handleProductChange = async value => {
    Object.entries(lineFlagsFor(value)).forEach(([flag, on]) => set(flag, on));
    set("amount", "");
    set("product_price_id", null);
    set("cost", "");
    if (!value) return;
    if (isSpecialLineValue(value)) {
      ["term", "quantity"].forEach(field => set(field, ""));
      ["license_start_date", "license_end_date"].forEach(field =>
        set(field, null)
      );
      return;
    }
    const product = await queryClient.fetchQuery(productQuery(value));
    if (!getValues(path("quantity"))) set("quantity", "1");
    if (isPricingEnabled) {
      // A new product resets the line to that product's defaults: its current
      // price, its shortest allowed term and its Default Cost.
      const current = product.prices.find(price => price.is_current);
      set("product_price_id", current?.id ?? null);
      set("amount", current ? String(current.amount) : "");
      set("cost", product.default_cost ?? "");
      set("price_basis", product.price_basis);
      const lowestTerm = Math.min(...product.allowed_terms);
      set("term", Number.isFinite(lowestTerm) ? String(lowestTerm) : "");
      recalculateEndDate(line.license_start_date, lowestTerm);
      return;
    }
    // Full price precision keeps totals equal to the importer's, which
    // multiplies list_price without rounding first.
    set("amount", String(product.list_price));
    if (!getValues(path("term"))) set("term", "1");
  };

  const handlePriceChange = priceId => {
    const price = lineProduct?.prices.find(p => p.id === priceId);
    set("amount", price ? String(price.amount) : "");
  };

  const handleTermChange = term => {
    recalculateEndDate(line.license_start_date, term);
  };

  return (
    <TableRow>
      <TableCell className={CELL}>
        <FormCombobox
          name={path("product_id")}
          placeholder="Pick a Product"
          options={options}
          renderOption={renderProductOption}
          clearable
          onValueChange={handleProductChange}
        />
      </TableCell>
      <TableCell className={CELL}>
        {isPricingEnabled && lineProduct ? (
          <PricedAmountField
            index={index}
            line={line}
            product={lineProduct}
            isFlagged={Boolean(get(errors, path("product_price_id")))}
            onPriceChange={handlePriceChange}
          />
        ) : (
          <FormInput
            name={path("amount")}
            aria-label={`Invoice line ${index + 1} amount`}
            inputMode="decimal"
            prefix="$"
            disabled={!special}
          />
        )}
      </TableCell>
      <TableCell className={CELL}>
        {!special && isPricingEnabled && lineProduct && (
          <AllowedTermField
            index={index}
            line={line}
            product={lineProduct}
            isFlagged={Boolean(get(errors, path("term")))}
            onTermChange={handleTermChange}
          />
        )}
        {!special && !(isPricingEnabled && lineProduct) && (
          <FormInput
            name={path("term")}
            aria-label={`Invoice line ${index + 1} term`}
            inputMode="numeric"
            disabled={Boolean(isPricingEnabled)}
            onValueChange={handleTermChange}
          />
        )}
      </TableCell>
      {isPricingEnabled && (
        <TableCell className={CELL}>
          {!special && (
            <FormInput
              name={path("cost")}
              aria-label={`Invoice line ${index + 1} cost`}
              inputMode="decimal"
              prefix="$"
            />
          )}
        </TableCell>
      )}
      <TableCell className={CELL}>
        {!special && (
          <FormInput
            name={path("quantity")}
            aria-label={`Invoice line ${index + 1} quantity`}
            inputMode="numeric"
          />
        )}
      </TableCell>
      <TableCell className={CELL}>
        {!special && (
          <FormDatePicker
            name={path("license_start_date")}
            aria-label={`Invoice line ${index + 1} license start date`}
            onValueChange={startDate =>
              recalculateEndDate(startDate, line.term)
            }
          />
        )}
      </TableCell>
      <TableCell className={CELL}>
        {!special && (
          <FormDatePicker
            name={path("license_end_date")}
            aria-label={`Invoice line ${index + 1} license end date`}
          />
        )}
      </TableCell>
      <TableCell
        className={cn(CELL, "tw:text-right tw:leading-8 tw:font-medium")}
      >
        {buildPrice(getInvoicesRowAmount(line))}
      </TableCell>
      <TableCell className={CELL}>
        <RemoveButton
          label={`Remove invoice line ${index + 1}`}
          onClick={() => onRemove(index)}
        />
      </TableCell>
    </TableRow>
  );
};

const optionShape = PropTypes.shape({
  key: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  text: PropTypes.string,
});

InvoiceRow.propTypes = {
  index: PropTypes.number.isRequired,
  productOptions: PropTypes.arrayOf(optionShape).isRequired,
  disabledProductOptions: PropTypes.arrayOf(optionShape).isRequired,
  hasOtherOverride: PropTypes.bool.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default InvoiceRow;
